import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

import CreateOrderButton from "layouts/product-management-details/CreateOrderButton";
import CreateComplaintButton from "layouts/product-management-details/CreateComplaintButton";

import DataTable from "examples/Tables/DataTable";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Images
import brandDark from "assets/images/somosure/logo.png";

import MDActionConfigurator from "components/MDActionConfigurator";
import AddonSection from "layouts/product-management-details/AddonSection";
import ProductRequirementsSection from "layouts/product-management-details/ProductRequirementsSection";
import ComplaintRequirementsSection from "layouts/product-management-details/ComplaintRequirementsSection";

import {
  getSingleProduct,
  updateProduct,
  createProduct,
} from "actions/productActions";
import { getServiceList } from "actions/serviceActions";
import { getSupplierList } from "actions/supplierActions";
import { getProfile } from "actions/userActions";

function ProductManagementDetails() {
  const configuratorRef = useRef(null);
  const dispatch = useDispatch();
  const { id } = useParams();
  const [selectServices, setSelectServices] = useState([]);
  const [selectSuppliers, setSelectSuppliers] = useState([]);
  const [currentState, setCurrentState] = useState("");
  const [currentData, setCurrentData] = useState({
    _id: "",
    productNumber: "",
    productName: "",
    supplier: {},
    service: {},
    description: "",
    insuredItems: [],
    amount: 0,
    adminNotes: "",
    active: true,
  });

  const getMe = useSelector((state) => state.getMe);
  const { me } = getMe;

  useEffect(() => {
    // if (me?.length === 0) {
    dispatch(getProfile());
    // }
  }, [dispatch]);

  const singleProduct = useSelector((state) => state.singleProduct);
  const { productSingle } = singleProduct;

  const listServices = useSelector((state) => state.listServices);
  const { servicesList } = listServices;

  const listSuppliers = useSelector((state) => state.listSuppliers);
  const { suppliersList } = listSuppliers;

  useEffect(() => {
    dispatch(getSingleProduct(id));
  }, [dispatch, productSingle?.length, id]);

  useEffect(() => {
    if (servicesList?.length === 0) {
      dispatch(getServiceList());
    }
    setSelectServices(
      servicesList?.map((item) => ({
        value: item._id,
        name: item.mainService + " - " + item.service,
      }))
    );
  }, [dispatch, servicesList?.length]);

  useEffect(() => {
    if (suppliersList?.length === 0) {
      dispatch(getSupplierList());
    }
    setSelectSuppliers(
      suppliersList?.map((item) => ({
        value: item._id,
        name: item.supplierName,
      }))
    );
  }, [dispatch, suppliersList?.length]);

  const handleToggleBackdrop = (item, state) => {
    setCurrentData(item);
    setCurrentState(state);
    if (configuratorRef.current) {
      configuratorRef.current.handleToggleConfigurator();
    }
  };

  const handleSubmitProduct = (e) => {
    if (currentState === "edit") {
      dispatch(updateProduct(currentData?._id, e));
    } else {
      dispatch(createProduct(e));
    }
    handleToggleBackdrop({}, "");
  };

  const getGrandTotal = () => {
    const addonsTotal = productSingle?.addons?.reduce((acc, item) => {
      return acc + item.addonAmount;
    }, 0);
    return addonsTotal + productSingle?.amount;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar
        title={`${productSingle?.supplier?.supplierName}'s ${productSingle?.productName} Product Details`}
      />
      <MDBox mt={6} mb={6}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={5} lineHeight={0}>
                <MDBox display="flex" justifyContent="space-between">
                  <MDBox display="flex" alignItems="center">
                    <MDBox
                      component="img"
                      src={brandDark}
                      alt="Brand"
                      width="9rem"
                    />
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDBox
                      component="img"
                      src={productSingle?.supplier?.supplierLogo}
                      alt="Brand"
                      width="5rem"
                    />
                  </MDBox>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={10}>
                  <MDBox>
                    <MDTypography variant="h6">
                      Vertical Lakes Fisheries
                    </MDTypography>
                    <MDTypography variant="h6">Nairobi, Kenya</MDTypography>
                    <MDTypography variant="h6">
                      Email:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        verticalls@mail.com
                      </MDTypography>
                    </MDTypography>
                    <MDTypography variant="h6">
                      Phone:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        +254 700 112344
                      </MDTypography>
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign="right">
                    <MDTypography variant="h6">
                      {productSingle?.supplier?.supplierName}
                    </MDTypography>
                    <MDTypography variant="h6">
                      {productSingle?.supplier?.supplierAddress}
                    </MDTypography>
                    <MDTypography variant="h6">
                      Email:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {productSingle?.supplier?.supplierEmail}
                      </MDTypography>
                    </MDTypography>
                    <MDTypography variant="h6">
                      Phone:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {productSingle?.supplier?.supplierPhone}
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={5}>
                  <MDBox>
                    <MDTypography variant="h6">Product No.</MDTypography>
                    <MDTypography
                      variant="button"
                      color="text"
                      fontWeight="regular"
                    >
                      #{productSingle?.productNumber}
                    </MDTypography>
                    <MDTypography variant="h6" mt={1}>
                      Product Name
                    </MDTypography>
                    <MDTypography
                      variant="button"
                      color="text"
                      fontWeight="regular"
                    >
                      {productSingle?.productName}
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign="right">
                    <MDTypography variant="h6">
                      Created:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {moment(productSingle?.created_at).format("DD/MM/YYYY")}
                      </MDTypography>
                    </MDTypography>
                    <MDTypography variant="h6">
                      Service:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {productSingle?.service?.mainService} -{" "}
                        {productSingle?.service?.service}
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3} mb={2}>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "Description",
                          accessor: "description",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: "Insured Items",
                          accessor: "insuredItems",
                          width: "30%",
                          align: "left",
                        },

                        {
                          Header: "Premium Amount",
                          accessor: "amount",
                          width: "20%",
                          align: "center",
                        },
                        {
                          Header: "Active",
                          accessor: "active",
                          width: "10%",
                          align: "center",
                        },
                        {
                          Header: "action",
                          accessor: "action",
                          width: "10%",
                          align: "center",
                        },
                      ],

                      rows: [
                        {
                          description: (
                            <MDBox maxWidth={300}>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                {productSingle?.description}
                              </MDTypography>
                            </MDBox>
                          ),
                          insuredItems: (
                            <MDBox display="flex" flexDirection="column">
                              {productSingle?.insuredItems?.map(
                                (item, index) => (
                                  <MDTypography
                                    key={index}
                                    variant="caption"
                                    color="text"
                                    fontWeight="medium"
                                    mb={1}
                                  >
                                    ({index + 1}) {item}
                                  </MDTypography>
                                )
                              )}
                            </MDBox>
                          ),
                          amount: (
                            <MDTypography
                              variant="caption"
                              color="text"
                              fontWeight="medium"
                            >
                              Ksh. {productSingle?.amount} /{" "}
                              {productSingle?.premiumPercentage}%
                            </MDTypography>
                          ),
                          active: (
                            <MDTypography
                              variant="caption"
                              color="text"
                              fontWeight="medium"
                            >
                              {productSingle?.active ? (
                                <MDBox ml={-1}>
                                  <MDBadge
                                    badgeContent="active"
                                    color="dark"
                                    variant="gradient"
                                    size="sm"
                                  />
                                </MDBox>
                              ) : (
                                <MDBox ml={-1}>
                                  <MDBadge
                                    badgeContent="inactive"
                                    color="success"
                                    variant="gradient"
                                    size="sm"
                                  />
                                </MDBox>
                              )}
                            </MDTypography>
                          ),
                          action: (
                            <>
                              {(me?.role === "super_admin" ||
                                me?.role === "admin") && (
                                <MDTypography
                                  component="a"
                                  href="#"
                                  variant="caption"
                                  color="text"
                                  fontWeight="medium"
                                  onClick={() =>
                                    handleToggleBackdrop(productSingle, "edit")
                                  }
                                >
                                  Edit
                                </MDTypography>
                              )}
                            </>
                          ),
                        },
                      ],
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>

                <MDBox mb={5}>
                  <MDTypography variant="h6">Admin Note:</MDTypography>
                  <MDBox p={2} mt="auto">
                    {productSingle?.adminNotes ? (
                      <MDTypography
                        variant="caption"
                        color="text"
                        fontWeight="medium"
                      >
                        {productSingle?.adminNotes}
                      </MDTypography>
                    ) : (
                      <MDTypography variant="caption" color="text">
                        N / A
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>

                <AddonSection productSingle={productSingle} />
                <ProductRequirementsSection productSingle={productSingle} />
                <ComplaintRequirementsSection productSingle={productSingle} />

                <MDBox display="flex" justifyContent="space-between" mb={5}>
                  <MDBox>
                    <MDTypography variant="body2" py={2}>
                      Start an order with this product:{" "}
                    </MDTypography>
                    <CreateOrderButton selectProduct={productSingle} />
                  </MDBox>

                  {/* <MDBox>
                    <MDTypography variant="body2" py={2}>
                      Make a complaint with this product:{" "}
                    </MDTypography>
                    <CreateComplaintButton selectProduct={productSingle} />
                  </MDBox> */}

                  <MDBox textAlign="flex-end" mr={5}>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      py={1}
                      justifyContent="flex-end"
                    >
                      <MDTypography variant="body2" color="text">
                        Product Total:{" "}
                      </MDTypography>
                      <MDTypography variant="h5" pl={3}>
                        Ksh. {productSingle?.amount}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      py={1}
                      justifyContent="flex-end"
                    >
                      <MDTypography variant="body2" color="text">
                        Total + Addons:{" "}
                      </MDTypography>
                      <MDTypography variant="h5" pl={3}>
                        Ksh. {getGrandTotal()}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      <MDActionConfigurator
        ref={configuratorRef}
        title="Product Config"
        onCompleteConfig={handleSubmitProduct}
        data={[
          {
            keyType: "_id",
            valueType: currentData?._id,
          },
          {
            labelType: "Product Number",
            placeholderType: "Enter Product Number",
            dataType: "text",
            keyType: "productNumber",
            valueType: currentData?.productNumber,
          },
          {
            labelType: "Product Name",
            placeholderType: "Enter Product Name",
            dataType: "text",
            keyType: "productName",
            valueType: currentData?.productName,
          },
          {
            labelType: "Supplier",
            placeholderType: "Select Supplier",
            dataType: "select",
            keyType: "supplier",
            valueType: currentData?.supplier?._id,
            options: selectSuppliers,
          },
          {
            labelType: "Service",
            placeholderType: "Select Service",
            dataType: "select",
            keyType: "service",
            valueType: currentData?.service?._id,
            options: selectServices,
          },
          {
            labelType: "Description",
            placeholderType: "Enter Description",
            dataType: "textarea",
            keyType: "description",
            valueType: currentData?.description,
          },
          {
            labelType: "Insured Items",
            placeholderType: "Enter Insured Items",
            dataType: "textinputchip",
            keyType: "insuredItems",
            valueType: currentData?.insuredItems,
            options: currentData?.insuredItems,
          },
          {
            labelType: "Premium Amount",
            placeholderType: "Enter Premium Amount",
            dataType: "number",
            keyType: "amount",
            valueType: currentData?.amount,
          },
          {
            labelType: "Admin Note",
            placeholderType: "Enter Admin Note",
            dataType: "textarea",
            keyType: "adminNotes",
            valueType: currentData?.adminNotes,
          },
          {
            labelType: "Active",
            placeholderType: "Active",
            dataType: "active",
            keyType: "active",
            valueType: currentData?.active,
          },
        ]}
        state={currentState}
      />

      <Footer />
    </DashboardLayout>
  );
}

export default ProductManagementDetails;
